import React, { useState } from 'react';
import { Card, CardBody, CardTitle, Col, Row, Badge } from 'reactstrap';
import { Link } from 'react-router-dom';
import { useAuth } from 'helpers/authContext';
import { isLoggedWithKeychain, getReputation } from 'services/dsteem-service';
import { useToast } from 'components/ui/use-toast';
import { keychainWitnessVote } from 'helpers/keychain';
import { witnessWifVote } from 'helpers/steem';
import LoginModal from 'features/user/userModals/LoginModal';
import useModal from 'helpers/useModal';

type Props = {
    witness: any;
    witnessAbove?: any;
    style?: any;
    reloadWitnesses?: () => void;
    userWitnessVotes?: string[];
};

export const WitnessListItem = ({ witness, witnessAbove, style, reloadWitnesses, userWitnessVotes = [] }: Props) => {
    const { user, autologin } = useAuth();
    const { toast } = useToast();
    const { isOpenLoginModal, toggleLoginModal } = useModal();

    const [isVoting, setIsVoting] = useState(false);
    const [requiredActive, setRequiredActive] = useState(false);
    const [pendingVoteStatus, setPendingVoteStatus] = useState<boolean | null>(null);

    const keychainLogin = isLoggedWithKeychain(autologin);

    // Check if the current user has voted for this witness
    const isVoted = userWitnessVotes.includes(witness.owner);

    const copyLink = () => {
        const baseUrl = window.location.origin;
        const link = `${baseUrl}/witnesses?op=voteWitness&id=${witness.owner}`;
        navigator.clipboard.writeText(link);
        toast({
            title: "Link Copied",
            description: "The witness voting link has been copied to your clipboard.",
            variant: "wild_green"
        });
    };

    const handleVote = async (voteStatus: boolean, wif = "") => {
        if (!user) {
            toast({ title: "Login required", description: "Please log in to vote for witnesses.", variant: "rose_red" });
            setPendingVoteStatus(voteStatus);
            toggleLoginModal();
            return;
        }

        setIsVoting(true);

        try {
            if (keychainLogin) {
                const success = await keychainWitnessVote(voteStatus, user, witness.owner);
                if (success === true) {
                    toast({
                        title: voteStatus ? "Witness Supported" : "Support Removed",
                        description: `Your vote for ${witness.owner} has been recorded.`,
                        variant: "wild_green"
                    });
                    if (reloadWitnesses) reloadWitnesses();
                } else {
                    toast({ title: "Error", description: "Failed to broadcast vote via Keychain", variant: "rose_red" });
                }
            } else {
                const res: any = await witnessWifVote(voteStatus, autologin, witness.owner, wif);
                if (res.success) {
                    toast({
                        title: voteStatus ? "Witness Supported" : "Support Removed",
                        description: `Your vote for ${witness.owner} has been recorded.`,
                        variant: "wild_green"
                    });
                    if (reloadWitnesses) reloadWitnesses();
                } else {
                    if (res?.result?.message?.toString().includes("Missing Active Authority") || res?.result?.message?.toString().includes("Missing Posting Authority")) {
                        setRequiredActive(true);
                        setPendingVoteStatus(voteStatus);
                        toggleLoginModal();
                    } else {
                        toast({ title: "Error", description: res.result, variant: "rose_red" });
                    }
                }
            }
        } catch (error) {
            console.error(error);
            toast({ title: "Error", description: "An unexpected error occurred.", variant: "rose_red" });
        } finally {
            setIsVoting(false);
        }
    };

    const voteButton = (
        <div 
          className={`icon icon-shape ${isVoted ? 'bg-voted' : 'bg-secondary2'} text-dark rounded-circle shadow upvote-icon d-flex align-items-center justify-content-center`} 
          style={{ cursor: isVoting ? 'default' : 'pointer', opacity: isVoting ? 0.7 : 1, width: '45px', height: '45px' }}
          onClick={() => !isVoting && handleVote(!isVoted)}
        >
          {isVoting ? (
            <span className="spinner m-0" />
          ) : (
            <i className="fas fa-angle-up" style={{ fontSize: '1.2rem' }}></i>
          )}
        </div>
    );

    const isInactive = witness.signingKey === "STM1111111111111111111111111111111114T1Anm";

    const formatVestsValue = (valueM: string | number) => {
        const num = typeof valueM === 'string' ? parseFloat(valueM) : valueM;
        if (num >= 1) {
            return num.toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 }) + "M";
        } else {
            return (num * 1000).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + "K";
        }
    };

    return (
        <div style={style}>
            <Card className="shadow-none border proposal-card" style={{ backgroundColor: 'var(--bg-light)', borderRadius: '15px', opacity: isInactive ? 0.7 : 1 }}>
                <CardBody className="p-3">
                    <Row className="align-items-center m-0 flex-nowrap">
                        <Col className="col-auto pr-0 d-flex justify-content-start align-items-center" style={{ width: '55px' }}>
                            <h4 className="font-weight-bold mb-0 text-muted">
                                {isInactive ? "" : `#${witness.rank}`}
                            </h4>
                        </Col>
                        <Col className="col" style={{ minWidth: '0' }}>
                            <Row className="align-items-center flex-nowrap">
                                <Col className="col-auto">
                                    <Link className="avatar avatar-sm rounded-circle" to={`/u/${witness.owner}`} target="_blank">
                                        <img alt="..." src={`https://steemitimages.com/u/${witness.owner}/avatar`} />
                                    </Link>
                                </Col>
                                <Col className="col pl-0">
                                    <CardTitle className="mb-0 d-flex align-items-center flex-wrap">
                                        <Link className="text mr-2 font-weight-bold" style={{ color: 'var(--letters)', textDecoration: isInactive ? 'line-through' : 'none' }} to={`/u/${witness.owner}`} target="_blank">
                                            {witness.owner} ({getReputation(witness.reputation)})
                                        </Link>
                                        <i
                                            className="fas fa-link ml-1 text-muted cursor-pointer"
                                            style={{ fontSize: '0.8rem' }}
                                            title="Copy Vote Link"
                                            onClick={copyLink}
                                        />
                                    </CardTitle>
                                    <div className="mt-0 d-flex align-items-center flex-wrap">
                                        <a href={witness.url} target="_blank" rel="noreferrer" style={{ color: 'var(--speem)', fontSize: '0.75rem' }} className="d-flex align-items-center mr-2">
                                            <i className="fas fa-external-link-alt mr-1" />
                                            Witness Thread
                                        </a>
                                        {witnessAbove && (
                                            <span className="text-xs text-muted" style={{ fontSize: '0.7rem' }}>
                                                · (+{formatVestsValue(parseFloat(witnessAbove.approvalAmount) - parseFloat(witness.approvalAmount))} SP to reach #{witnessAbove.rank})
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-sm text-muted mb-0 mt-2 d-flex flex-wrap align-items-center">
                                        <Badge className="mr-2 border shadow-none badge-proposals" title="Total Votes">
                                            <i className="fas fa-thumbs-up mr-1" />
                                            {formatVestsValue(witness.approvalAmount)} SP
                                        </Badge>
                                        <Badge className="mr-2 border shadow-none badge-proposals" title="Missed Blocks">
                                            <i className="fas fa-ban mr-1" />
                                            {Number(witness.missedBlocks).toLocaleString()} Missed
                                        </Badge>
                                        <Badge className="mr-2 border shadow-none badge-proposals" title="Feed Price">
                                            <i className="fas fa-dollar-sign mr-1" />
                                            {witness.feedPrice}
                                        </Badge>
                                        <Badge className="mr-2 border shadow-none badge-proposals" title="Version">
                                            <i className="fas fa-code-branch mr-1" />
                                            v{witness.version}
                                        </Badge>
                                    </p>
                                </Col>
                            </Row>
                        </Col>
                        <Col className="col-auto d-flex align-items-center justify-content-center pl-2 pr-2">
                            {voteButton}
                        </Col>
                    </Row>
                </CardBody>
            </Card>

            <LoginModal
                isShowing={isOpenLoginModal}
                hide={toggleLoginModal}
                requiredActive={requiredActive}
                onSuccess={({ wif }) => {
                    if (wif && pendingVoteStatus !== null) {
                        handleVote(pendingVoteStatus, wif);
                    }
                }}
            />
        </div>
    );
}

export default WitnessListItem;
